import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
const LOGO_SRC = "/images/brand/preserve-earth-logo-navy.webp";
const LOGO_SRCSET = "/images/brand/preserve-earth-logo-navy-320.webp 320w, /images/brand/preserve-earth-logo-navy.webp 640w";
import { Button } from "@/components/ui/button";
import { openCookieSettings } from "@/lib/consent";
import { services } from "@/data/services";

type NavItem = { label: string; href: string };

function cssTimeMs(name: string) {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (raw.endsWith("ms")) return parseFloat(raw);
  if (raw.endsWith("s")) return parseFloat(raw) * 1000;
  return 0;
}

function menuCloseMs() {
  return cssTimeMs("--dur-words-out") + cssTimeMs("--dur-menu-out");
}

const DEFAULT_LINKS: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
];

export function SiteHeader({
  links = DEFAULT_LINKS,
  enquireHref = "/#enquire",
}: {
  links?: NavItem[];
  enquireHref?: string;
}) {
  const [present, setPresent] = useState(false);
  const [shown, setShown] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const closeMenu = () => {
    setShown(false);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    closeTimer.current = window.setTimeout(() => setPresent(false), reduce ? 0 : menuCloseMs());
  };
  const openMenu = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setServicesOpen(false);
    setPresent(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShown(true));
    });
  };

  const closeMenuRef = useRef(closeMenu);
  closeMenuRef.current = closeMenu;

  useEffect(() => {
    document.body.style.overflow = present ? "hidden" : "";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenuRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [present]);

  useEffect(() => {
    return () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-charcoal/5 bg-cream/85 backdrop-blur-md">
        <nav
          aria-label="Primary"
          className="container-page flex h-20 items-center justify-between"
        >
          <Link to="/" className="flex items-center" aria-label="Preserve Earth: home">
            <img src={LOGO_SRC} srcSet={LOGO_SRCSET} sizes="(min-width: 768px) 128px, (min-width: 640px) 112px, 96px" width={640} height={322} alt="Preserve Earth" fetchPriority="high" className="h-12 w-auto sm:h-14 md:h-16" />
          </Link>

          <ul className="hidden items-center gap-10 text-sm font-medium uppercase tracking-wide md:flex">
            {links.map((l) => (
              <li key={l.label} className="relative">
                {l.href === "/services" ? <details className="group pe-nav-details">
                  <summary className="cursor-pointer list-none transition-colors hover:text-moss">{l.label}</summary>
                  <ul className="pe-menu-copy absolute left-0 top-full mt-4 min-w-52 border border-charcoal/10 bg-cream p-4 text-sm normal-case shadow-lg">
                    <li><a href={l.href} className="block py-2 hover:text-moss">{l.label}</a></li>
                    {services.map((s) => (
                      <li key={s.slug}>
                        <Link to="/services/$slug" params={{ slug: s.slug }} className="block py-2 hover:text-moss">{s.title}</Link>
                      </li>
                    ))}
                  </ul>
                </details> :
                <a href={l.href} className="transition-colors hover:text-moss">
                  {l.label}
                </a>}
              </li>
            ))}
            <li>
              <a
                href={enquireHref}
                className="rounded-sm bg-moss px-5 py-2.5 text-cream transition-transform hover:bg-charcoal active:scale-95"
              >
                Enquire
              </a>
            </li>
          </ul>

          <div className="flex items-center gap-3 md:hidden">
            <a
              href={enquireHref}
              className="rounded-sm bg-moss px-4 py-2 text-xs font-medium uppercase tracking-wide text-cream"
            >
              Enquire
            </a>
            <button
              type="button"
              onClick={openMenu}
              aria-label="Open menu"
              aria-expanded={shown}
              className="flex size-10 touch-manipulation items-center justify-center rounded-sm ring-1 ring-charcoal/15"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </nav>
      </header>

      {present && createPortal(
        <div className="pe-mobile-nav fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true" aria-label="Site menu" data-state={shown ? "open" : "closed"}>
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeMenu}
            className="pe-mobile-scrim absolute inset-0 bg-charcoal/50"
          />
          <div className="pe-mobile-panel absolute inset-y-0 right-0 flex w-[82%] max-w-xs flex-col overflow-y-auto bg-cream p-6 shadow-xl">
              <div className="mb-8 flex items-center justify-between">
                <img src={LOGO_SRC} srcSet={LOGO_SRCSET} sizes="80px" width={640} height={322} alt="Preserve Earth" className="h-10 w-auto" />
                <button
                  type="button"
                  onClick={closeMenu}
                  aria-label="Close menu"
                  className="flex size-10 items-center justify-center rounded-sm ring-1 ring-charcoal/15"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
              <ul className="space-y-1 text-lg">
                {links.map((l) => (
                  <li key={l.label}>
                    {l.href === "/services" ? (
                      <>
                        <div className="flex items-center border-b border-charcoal/10">
                          <a
                            href={l.href}
                            onClick={closeMenu}
                            className="flex-1 py-4 font-serif text-charcoal"
                          >
                            {l.label}
                          </a>
                          <button
                            type="button"
                            onClick={() => setServicesOpen((v) => !v)}
                            aria-expanded={servicesOpen}
                            aria-controls="mobile-services-submenu"
                            aria-label="Toggle services submenu"
                            className="flex size-10 shrink-0 items-center justify-center text-charcoal/70"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={servicesOpen ? "rotate-180 transition-transform duration-menu-in" : "transition-transform duration-menu-out"}>
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </button>
                        </div>
                        <div className="pe-submenu" data-open={servicesOpen ? "true" : "false"}>
                        <ul id="mobile-services-submenu" inert={!servicesOpen ? true : undefined}>
                          {services.map((s) => (
                            <li key={s.slug}>
                              <Link
                                to="/services/$slug"
                                params={{ slug: s.slug }}
                                onClick={closeMenu}
                                className="block border-b border-charcoal/10 py-3 pl-4 font-serif text-base text-charcoal"
                              >
                                {s.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        </div>
                      </>
                    ) : (
                      <a
                        href={l.href}
                        onClick={closeMenu}
                        className="block border-b border-charcoal/10 py-4 font-serif text-charcoal"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              <a
                href={enquireHref}
                onClick={closeMenu}
                className="mt-8 rounded-sm bg-moss py-4 text-center text-sm font-medium uppercase tracking-wide text-cream"
              >
                Enquire
              </a>
              <a
                href="mailto:info@preserveearth.co.uk"
                className="mt-4 text-center text-sm text-charcoal/70"
              >
                info@preserveearth.co.uk
              </a>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-charcoal/10">
      <div className="container-page flex flex-col items-center justify-between gap-6 py-10 md:flex-row">
        <p className="text-center text-xs leading-relaxed text-charcoal/70 md:text-left">
          © {new Date().getFullYear()} Preserve Earth. Preserve Earth is a trading name of
          RBM London Limited · Company No. 15482817. All rights reserved.
        </p>
        <ul className="flex flex-wrap justify-center gap-6 text-xs font-medium text-charcoal/70">
          <li><Link to="/about" className="hover:text-moss">About</Link></li>
          <li><Link to="/company-info" className="hover:text-moss">Company Info</Link></li>
          <li><Link to="/privacy-policy" className="hover:text-moss">Privacy</Link></li>
          <li><Link to="/terms" className="hover:text-moss">Terms</Link></li>
          <li><Link to="/sustainability-policy" className="hover:text-moss">Sustainability</Link></li>
          <li><Link to="/modern-slavery-act-policy" className="hover:text-moss">Modern Slavery</Link></li>
          <li><Link to="/csr-policy" className="hover:text-moss">CSR</Link></li>
          <li><a href="https://www.linkedin.com/company/preserveearth" target="_blank" rel="noopener" aria-label="Preserve Earth on LinkedIn" className="hover:text-moss">LinkedIn</a></li>
          <li><a href="tel:+443300432265" aria-label="Call Preserve Earth on 0330 043 2265" className="hover:text-moss">0330 043 2265</a></li>
          <li><Button variant="link" onClick={openCookieSettings} className="h-auto p-0 text-xs font-medium text-charcoal/70 hover:text-moss">Cookie settings</Button></li>

        </ul>
      </div>
    </footer>
  );
}
